// ============================================================================
// MEDIA
// ============================================================================

export * from "./media";
export * from "./homepage";
export * from "./journeys";
import type { MediaResponse } from "./media";

// ============================================================================
// ENUMS & CONSTANTS
// ============================================================================

export const CONTINENTS = [
  "asia",
  "africa",
  "south-america",
  "north-america",
  "oceania",
  "europe",
] as const;
export type Continent = (typeof CONTINENTS)[number];

export const DIFFICULTY_LEVELS = ["easy", "medium", "hard"] as const;
export type DifficultyLevel = (typeof DIFFICULTY_LEVELS)[number];

export const TRIP_TYPES = ["guided", "private", "resort"] as const;
export type TripType = (typeof TRIP_TYPES)[number];

export const TRIP_KINDS = ["tour", "experience"] as const;
export type TripKind = (typeof TRIP_KINDS)[number];

// Booking axis (Pre-launch Area 3). Deliberately separate from `tripKind` (content axis):
//   - fixed_departures = customer picks from scheduled tripDates (the dropdown).
//   - on_request       = open calendar, any allowed weekday + lead time; a private tripDate
//                        is materialized at lead→order conversion (see orderCreate.ts).
// Use isOnRequestBooking(trip) for EVERY booking-axis branch — never read tripKind for booking.
export const FULFILLMENT_MODES = ["fixed_departures", "on_request"] as const;
export type FulfillmentMode = (typeof FULFILLMENT_MODES)[number];

export const TRIP_STYLES = [
  "relaxation",
  "cultural",
  "adventure",
  "family",
  "foodie",
  "wellness",
  "nature",
  "cruises",
] as const;
export type TripStyle = (typeof TRIP_STYLES)[number];

// The public-facing style taxonomy (hero quick-filter + /trips sidebar chips). `family` and
// `wellness` are valid enum values but stay admin-only — no public nav surfaces them, so keeping
// them out of the public chips avoids dead filters. One source of truth: hero == sidebar.
export const PUBLIC_TRIP_STYLES = [
  "cultural",
  "adventure",
  "relaxation",
  "cruises",
  "foodie",
  "nature",
] as const satisfies readonly TripStyle[];

export const DESTINATION_REGIONS = ["northern", "central", "southern"] as const;
export type DestinationRegion = (typeof DESTINATION_REGIONS)[number];

export const TRIP_STATUSES = ["draft", "published", "archived"] as const;
export type TripStatus = (typeof TRIP_STATUSES)[number];

// Departure two-axis status (Vlna 2 / Block 4.1, R1). The old single `status` conflated the ops
// decision ("will it run?") with how full it is. Split into:
//   - lifecycle: STORED, manual/ops-driven decision about the departure.
//   - occupancy: DERIVED on read from bookedCount vs capacity — never stored (see occupancyLevel).
export const TRIP_DATE_LIFECYCLES = [
  "draft", // not selling yet (internal hold)
  "tentative", // selling, not yet go-decided (default for a fresh departure)
  "guaranteed", // confirmed it runs
  "wont_run", // ops decided it will NOT run (under-booked) — terminal, non-bookable
  "cancelled", // cancelled after selling — terminal, non-bookable
] as const;
export type TripDateLifecycle = (typeof TRIP_DATE_LIFECYCLES)[number];

// The only lifecycles a customer may book onto — what the public web + journeys expose (R3).
export const BOOKABLE_LIFECYCLES = ["tentative", "guaranteed"] as const;
// Terminal lifecycles a departure will not run under — excluded from ops worklists/analytics.
export const NON_RUNNING_LIFECYCLES = ["wont_run", "cancelled"] as const;

export const TRIP_DATE_LIFECYCLE_LABELS: Record<TripDateLifecycle, string> = {
  draft: "Draft",
  tentative: "Tentative",
  guaranteed: "Guaranteed",
  wont_run: "Won't run",
  cancelled: "Cancelled",
};

// Tailwind (admin palette) badge classes — consumed by Departures list, velín, Season board (R6).
export const TRIP_DATE_LIFECYCLE_BADGES: Record<TripDateLifecycle, string> = {
  draft: "bg-gray-100 text-gray-600",
  tentative: "bg-blue-100 text-blue-700",
  guaranteed: "bg-emerald-100 text-emerald-700",
  wont_run: "bg-orange-100 text-orange-700",
  cancelled: "bg-red-100 text-red-600",
};

// Derived occupancy (NOT stored) — how full the departure is.
export const OCCUPANCY_LEVELS = ["open", "few_spots", "full"] as const;
export type OccupancyLevel = (typeof OCCUPANCY_LEVELS)[number];

export const OCCUPANCY_LABELS: Record<OccupancyLevel, string> = {
  open: "Open",
  few_spots: "Few spots",
  full: "Full",
};

export const OCCUPANCY_BADGES: Record<OccupancyLevel, string> = {
  open: "bg-blue-100 text-blue-700",
  few_spots: "bg-amber-100 text-amber-700",
  full: "bg-gray-100 text-gray-600",
};

// ============================================================================
// GUIDE SCHEDULE (Vlna 2 / Block 4.2 — rozvrh průvodců / HRABE)
// ============================================================================

// A guide's role on a departure. `lead` mirrors to tripDates.guideId, the first `co` to
// sharedGuideId (when confirmed); `backup` is a standby (e.g. the owner) never mirrored.
export const GUIDE_ASSIGNMENT_ROLES = ["lead", "co", "backup"] as const;
export type GuideAssignmentRole = (typeof GUIDE_ASSIGNMENT_ROLES)[number];

export const GUIDE_ASSIGNMENT_ROLE_LABELS: Record<GuideAssignmentRole, string> = {
  lead: "Lead guide",
  co: "Co-guide",
  backup: "Backup",
};

// tentative = internal planning (not customer-visible); confirmed = mirrored + revealed.
export const GUIDE_ASSIGNMENT_STATUSES = ["tentative", "confirmed"] as const;
export type GuideAssignmentStatus = (typeof GUIDE_ASSIGNMENT_STATUSES)[number];

export const GUIDE_ASSIGNMENT_STATUS_LABELS: Record<GuideAssignmentStatus, string> = {
  tentative: "Tentative",
  confirmed: "Confirmed",
};

export const GUIDE_AVAILABILITY_REASONS = ["vacation", "sick", "blocked", "other"] as const;
export type GuideAvailabilityReason = (typeof GUIDE_AVAILABILITY_REASONS)[number];

export const GUIDE_AVAILABILITY_REASON_LABELS: Record<GuideAvailabilityReason, string> = {
  vacation: "Vacation",
  sick: "Sick",
  blocked: "Blocked",
  other: "Other",
};

// Conflict severities the schedule engine emits (lib/guideSchedule). red = hard (overlap /
// unavailable), orange = soft "verify the connection" (tight gap between distant locations).
export const SCHEDULE_CONFLICT_SEVERITIES = ["red", "orange"] as const;
export type ScheduleConflictSeverity = (typeof SCHEDULE_CONFLICT_SEVERITIES)[number];

export const SCHEDULE_CONFLICT_KINDS = [
  "overlap", // two departures of the same guide overlap in time → red
  "availability", // assignment overlaps a vacation/sick window → red
  "tight_gap", // too few days to get from prev end-location to next start-location → orange
  "home_gap", // too few days from home location to the first assignment → orange
] as const;
export type ScheduleConflictKind = (typeof SCHEDULE_CONFLICT_KINDS)[number];

// Default minimum days needed between two departures when no location_gaps row overrides it.
// Same location → 0 (no travel), different → 1. Gap-check is skipped when either location is null.
export const DEFAULT_MIN_GAP_DAYS = 1;
export const SAME_LOCATION_MIN_GAP_DAYS = 0;

export const ORDER_STATUSES = [
  "inquiry",
  "quoted",
  "confirmed",
  "deposit_paid",
  "fully_paid",
  "completed",
  "cancelled",
] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];

// Statuses where the order does NOT yet hold seats on a tripDate.
export const ORDER_PRE_HOLD_STATUSES = ["inquiry", "quoted"] as const;
// Statuses where the order DOES hold seats (bookedCount counts these).
export const ORDER_HOLD_STATUSES = ["confirmed", "deposit_paid", "fully_paid"] as const;
// Statuses that release any held seats.
export const ORDER_RELEASE_STATUSES = ["cancelled"] as const;

// Lead (inquiry funnel) statuses — a separate pipeline from orders.status.
export const LEAD_STATUSES = [
  "received",
  "quoted",
  "call_back",
  "dormant",
  "sold",
  "lost",
] as const;
export type LeadStatus = (typeof LEAD_STATUSES)[number];

export const USER_ROLES = ["admin", "editor", "marketing", "sales", "readonly"] as const;
export type UserRole = (typeof USER_ROLES)[number];

// Agent proposal approval gate (Block 3, rozhodnutí R-C). Single source of truth shared by the
// API approve route (`routes/admin/agent.ts`) and the admin `ProposalCard` so the gate can never
// drift between server and UI. money/send → admin/sales; write (tasks/notes/date-change) →
// admin/editor/sales; readonly/marketing may chat + raise proposals but never approve.
export const PROPOSAL_MONEY_APPROVE_ROLES: readonly UserRole[] = ["admin", "sales"];
export const PROPOSAL_WRITE_APPROVE_ROLES: readonly UserRole[] = ["admin", "editor", "sales"];

export function proposalApproveRoles(tier: string): readonly UserRole[] {
  return tier === "money" ? PROPOSAL_MONEY_APPROVE_ROLES : PROPOSAL_WRITE_APPROVE_ROLES;
}

/** Whether `role` may approve/execute a proposal of `tier`. Used server- and client-side. */
export function canApproveProposal(tier: string, role: string): boolean {
  return proposalApproveRoles(tier).includes(role as UserRole);
}

export const ORDER_ITEM_KINDS = [
  "trip",
  "custom",
  "addon",
  "flight",
  "insurance",
  "discount",
  "fee",
] as const;
export type OrderItemKind = (typeof ORDER_ITEM_KINDS)[number];

export const ORDER_ITEM_KIND_LABELS: Record<OrderItemKind, string> = {
  trip: "Trip",
  custom: "Custom",
  addon: "Add-on",
  flight: "Flight",
  insurance: "Insurance",
  discount: "Discount",
  fee: "Fee",
};

/** Entry-paperwork kind (Block 5.4). Display labels are the same for operators everywhere; the
 *  customer-facing portal keeps its own reassuring status copy, so only the KIND label is shared. */
export const ENTRY_TASK_KINDS = ["arrival_card", "visa"] as const;
export type EntryTaskKind = (typeof ENTRY_TASK_KINDS)[number];

export const ENTRY_TASK_KIND_LABELS: Record<EntryTaskKind, string> = {
  arrival_card: "Arrival card",
  visa: "Visa",
};

export const COMMUNICATION_TYPES = [
  "email",
  "phone",
  "note",
  "sms",
  "status_change",
  "payment",
] as const;
export type CommunicationType = (typeof COMMUNICATION_TYPES)[number];

export const COMMUNICATION_DIRECTIONS = ["inbound", "outbound", "internal"] as const;
export type CommunicationDirection = (typeof COMMUNICATION_DIRECTIONS)[number];

export const ACTIVITY_TYPES = [
  "water",
  "adventure",
  "culture",
  "nature",
  "food",
  "wellness",
] as const;
export type ActivityType = (typeof ACTIVITY_TYPES)[number];

// ============================================================================
// SUPPLIERS & SERVICE CATALOG (Block 2 — operations)
// ============================================================================

// The kind of company we buy from (describes the supplier, not the booking).
export const SUPPLIER_TYPES = [
  "hotel",
  "airline",
  "insurance",
  "transport",
  "guide",
  "activity",
  "visa",
  "other",
] as const;
export type SupplierType = (typeof SUPPLIER_TYPES)[number];

// How we settle with a supplier — drives which AP obligations the engine watches.
export const SUPPLIER_PAYMENT_PROFILES = ["invoice", "cash_offline", "online", "prepaid"] as const;
export type SupplierPaymentProfile = (typeof SUPPLIER_PAYMENT_PROFILES)[number];

// Kind of a booked service — shared between `supplier_components` (catalog) and
// `service_bookings` (B2.1). Distinct axis from SUPPLIER_TYPES (a "transport"
// supplier can sell both "transfer" and "flight" components).
export const SERVICE_KINDS = [
  "flight",
  "hotel",
  "insurance",
  "activity",
  "visa",
  "transfer",
  "other",
] as const;
export type ServiceKind = (typeof SERVICE_KINDS)[number];

// Whether a cost is fixed per departure (chartered bus) or scales per pax (insurance).
export const COST_SCOPES = ["per_departure", "per_pax"] as const;
export type CostScope = (typeof COST_SCOPES)[number];

// How a service gets booked (E2E-014). `confirm_required` = an external supplier we must REQUEST
// and wait on (send a booking-request email, watch a confirm deadline, status draft→requested→
// confirmed). `on_demand` = our own / always-available inventory (our boat, an owned activity) that
// needs no supplier handshake: no outbound email, no confirm deadline, counts as ready by default.
// The fact lives on the supplier (the natural home of "our own boat"), can be overridden per
// catalog component, and is SNAPSHOTTED onto each `service_bookings` row — every engine (deadline
// scan / readiness / fulfillment / date-change / agent) reads it off the booking, not via a join.
export const BOOKING_MODES = ["confirm_required", "on_demand"] as const;
export type BookingMode = (typeof BOOKING_MODES)[number];

// Trip "kusovník": where a template component seeds a booking — once per order (per-pax
// services every traveller needs) or once per departure (a shared bus/guide). See §5.5.
export const TRIP_COMPONENT_SEED_TARGETS = ["order", "departure"] as const;
export type TripComponentSeedTarget = (typeof TRIP_COMPONENT_SEED_TARGETS)[number];

// Lifecycle of a supplier service booking (vision §5.5).
export const SERVICE_BOOKING_STATUSES = [
  "draft",
  "requested",
  "confirmed",
  "paid",
  "cancelled",
] as const;
export type ServiceBookingStatus = (typeof SERVICE_BOOKING_STATUSES)[number];

// Flight allotment (Block 4.10) — a held block of seats moves active → released (unsold returned
// at FOC) → ticketed (all issued); cancelled if the block is dropped.
export const FLIGHT_ALLOTMENT_STATUSES = ["active", "released", "ticketed", "cancelled"] as const;
export type FlightAllotmentStatus = (typeof FLIGHT_ALLOTMENT_STATUSES)[number];

// ============================================================================
// CALCULATIONS (Block 5.1 — trip/order pricing: costs → markup → sell price)
// ============================================================================

// `active` = the source of truth for apply/REAL (max 1 per anchor, enforced by the
// activate route which demotes siblings to draft).
export const CALC_STATUSES = ["draft", "active", "archived"] as const;
export type CalcStatus = (typeof CALC_STATUSES)[number];

// A LABEL only — guided trips say "markup", FIT-DMC quotes say "commission", but the
// arithmetic is identical (netto × (1 + marginPct), verified from the Go2 sheets).
export const CALC_MARGIN_MODES = ["markup", "commission"] as const;
export type CalcMarginMode = (typeof CALC_MARGIN_MODES)[number];

export const CALC_CATEGORIES = [
  "accommodation",
  "transport",
  "guide",
  "entrance",
  "flight_domestic",
  "insurance",
  "misc",
] as const;
export type CalcCategory = (typeof CALC_CATEGORIES)[number];

export const CALC_CATEGORY_LABELS: Record<CalcCategory, string> = {
  accommodation: "Accommodation",
  transport: "Transport",
  guide: "Guide",
  entrance: "Entrances",
  flight_domestic: "Domestic flights",
  insurance: "Insurance",
  misc: "Misc",
};

// How an item cost multiplies: per_pax × pax, per_group fixed, per_room × ceil(pax/occupancy).
// per_room is a calculation-only concept (the single-room supplement falls out of it);
// it maps to per_departure when pushed to the operational kusovník.
export const CALC_SCOPES = ["per_pax", "per_group", "per_room"] as const;
export type CalcScope = (typeof CALC_SCOPES)[number];

export const CALC_SCOPE_LABELS: Record<CalcScope, string> = {
  per_pax: "Per pax",
  per_group: "Per group",
  per_room: "Per room",
};

// Default calc category for a catalog component's ServiceKind — used to prefill items
// imported from the kusovník and as the REAL report's fallback mapping (booking kind →
// category) when a booking has no componentId link.
export const SERVICE_KIND_TO_CALC_CATEGORY: Record<ServiceKind, CalcCategory> = {
  hotel: "accommodation",
  transfer: "transport",
  flight: "flight_domestic",
  activity: "entrance",
  insurance: "insurance",
  visa: "misc",
  other: "misc",
};

// ============================================================================
// TASKS / READINESS / DEADLINES (Block 2 — the polymorphic worklist, vision §6)
// ============================================================================

// What a task hangs on. Participant deadlines reuse "order" + meta.participantId.
// `customer`/`invoice` added in B3 řez 2 so journey `create_task` actions can hang tasks on
// those subjects directly (a win-back nudge on a customer, a follow-up on an invoice).
export const TASK_ENTITY_TYPES = [
  "order",
  "lead",
  "service_booking",
  "departure",
  "customer",
  "invoice",
  // Block 4.10 — flight-allotment deadlines (deposit / FOC / ticketing) hang on the block itself.
  "flight_allotment",
] as const;
export type TaskEntityType = (typeof TASK_ENTITY_TYPES)[number];

// Why the task exists. `deadline` is engine-derived; `readiness` is a manual checklist
// item on an order; the rest are human worklist entries.
export const TASK_KINDS = [
  "readiness",
  "deadline",
  "callback",
  "manual",
  "supplier_followup",
] as const;
export type TaskKind = (typeof TASK_KINDS)[number];

export const TASK_STATUSES = ["open", "done", "snoozed", "cancelled"] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];

export const TASK_SOURCES = ["auto", "manual", "workflow"] as const;
export type TaskSource = (typeof TASK_SOURCES)[number];

// State of a single readiness checklist point (lib/readiness.ts). `na` = not yet
// relevant at this order stage (e.g. balance before the order is confirmed).
export const READINESS_STATES = ["done", "todo", "warn", "na"] as const;
export type ReadinessState = (typeof READINESS_STATES)[number];

// ============================================================================
// FULFILLMENT — per-departure self-filling grid (Block 4.3, Téma 3)
// ============================================================================

// Rooming type per participant — ops-managed; drives the grid "Pokoj" roll-up and (later) the
// rooming list + single supplement.
export const ROOM_TYPES = ["single", "double", "twin", "triple", "other"] as const;
export type RoomType = (typeof ROOM_TYPES)[number];

export const ROOM_TYPE_LABELS: Record<RoomType, string> = {
  single: "Single",
  double: "Double",
  twin: "Twin",
  triple: "Triple",
  other: "Other",
};

// Short codes for the grid roll-up ("2×DBL, 1×SGL").
export const ROOM_TYPE_CODES: Record<RoomType, string> = {
  single: "SGL",
  double: "DBL",
  twin: "TWN",
  triple: "TRP",
  other: "OTH",
};

// Heads per room — the bridge from a catalogue room-type variant (Block 6.1 Řez 2b) to the
// calculation engine's per_room `occupancy`. A per-room cost is split across this many heads;
// the engine derives the single-room supplement from it (`cost × (1 − 1/occupancy)`). `single`
// occupancy 1 → no roommate to lose → zero derived supplement (a distinct single rate is its
// own variant). `other` defaults to 1 (treated as a whole-room charge).
export const ROOM_TYPE_OCCUPANCY: Record<RoomType, number> = {
  single: 1,
  double: 2,
  twin: 2,
  triple: 3,
  other: 1,
};

// One fulfillment cell — a derived (or manual) state, with optional n/total for per-pax columns.
export interface FulfillmentCell {
  state: ReadinessState;
  done?: number;
  total?: number;
}

// One row of the departure fulfillment grid = one live order on the departure. Per-pax columns
// (passport/insurance/visa) carry done/total; the rest are order-level cells.
export interface FulfillmentRow {
  orderId: string;
  orderNumber: string | null;
  leadName: string;
  pax: number;
  deposit: FulfillmentCell;
  balance: FulfillmentCell;
  passport: FulfillmentCell;
  insurance: FulfillmentCell;
  visa: FulfillmentCell; // state "na" when the departure has no visa services
  contract: FulfillmentCell;
  rooms: string; // roll-up of participant roomTypes, e.g. "2×DBL, 1×SGL"
  customized: boolean; // has an addon/custom order item (same set as velín customizationOrders)
}

export interface DepartureFulfillment {
  rows: FulfillmentRow[];
  aggregate: {
    orders: number;
    pax: number;
    missingDeposit: number;
    missingBalance: number;
    missingPassport: number;
    missingInsurance: number;
    missingVisa: number;
    missingContract: number;
    passportExpiring: number;
  };
}

// ============================================================================
// FINANCE — invoices, payments, supplier invoices (Block 2 B2.5)
// ============================================================================

export const INVOICE_TYPES = ["proforma", "final", "insurance", "credit_note"] as const;
export type InvoiceType = (typeof INVOICE_TYPES)[number];

export const INVOICE_STATUSES = ["issued", "paid", "cancelled"] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

// AR (in: customer → us) vs AP (out: us → supplier) on the unified ledger.
export const PAYMENT_DIRECTIONS = ["in", "out"] as const;
export type PaymentDirection = (typeof PAYMENT_DIRECTIONS)[number];

export const PAYMENT_METHODS = ["bank", "cash", "stripe", "qr", "other"] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

export const SUPPLIER_INVOICE_STATUSES = ["received", "approved", "paid", "disputed"] as const;
export type SupplierInvoiceStatus = (typeof SUPPLIER_INVOICE_STATUSES)[number];

// ============================================================================
// DOCUMENTS — generated PDFs + e-signature (Block 2 B2.4)
// ============================================================================

export const DOCUMENT_KINDS = [
  "contract",
  "voucher",
  "itinerary",
  "package",
  "supplier_confirmation",
  // Block 5.6: offer = sales PDF from trip+calculation+flights+country KB;
  // confirmation = agency counter-confirmation issued after signing (Řez 3).
  "offer",
  "confirmation",
  // Block 6.1 Řez 2c: insurance = the insurer's policy PDF, uploaded and attached to the order
  // (NOT generated from a template — it comes from the insurance company).
  "insurance",
  "other",
] as const;
export type DocumentKind = (typeof DOCUMENT_KINDS)[number];

/**
 * One "what's different about this order" deviation (Block 4.5, Téma 9) — derived from an addon/custom
 * order item OR a service booking carrying a customizationNote. See apps/api/src/lib/customizations.ts.
 */
export interface OrderCustomization {
  source: "item" | "service";
  kind: string;
  label: string;
  detail: string | null;
}

/**
 * Structured fields an AI extractor reads off a supplier confirmation PDF (Block 4.6) — a
 * read-only suggestion the admin reviews before attaching. All fields optional; an unreadable
 * PDF yields an empty object. Mirrors apps/api/src/lib/serviceDocParse.ts ParsedServiceDoc.
 */
export interface ParsedConfirmation {
  pnr?: string;
  flightNumber?: string;
  route?: string;
  departureTime?: string;
  arrivalTime?: string;
  policyNumber?: string;
  insurer?: string;
  supplierRef?: string;
  paxNames?: string;
  notes?: string;
  confirmationNumber?: string;
  supplierName?: string;
  checkIn?: string;
  checkOut?: string;
  roomInfo?: string;
  amount?: number;
  currency?: string;
}

/**
 * One ranked candidate when matching an incoming supplier PDF to a service booking on an order
 * (Block 4.6, inbox flow). The match is decided by deterministic code (not the model) and carries a
 * human-readable `reason` + `score` the operator sees before one-click attaching.
 */
export interface ConfirmationMatch {
  serviceBookingId: string;
  bookingLabel: string;
  score: number;
  reason: string;
}

// Who the document is for → portal/PWA visibility (vision §5.12).
export const DOCUMENT_AUDIENCES = ["client", "guide", "public", "internal"] as const;
export type DocumentAudience = (typeof DOCUMENT_AUDIENCES)[number];

export const DOCUMENT_STATUSES = ["draft", "sent", "viewed", "signed", "void"] as const;
export type DocumentStatus = (typeof DOCUMENT_STATUSES)[number];

export const DOCUMENT_LANGS = ["en", "sk"] as const;
export type DocumentLang = (typeof DOCUMENT_LANGS)[number];

export const SIGNATURE_STATUSES = ["sent", "viewed", "signed"] as const;
export type SignatureStatus = (typeof SIGNATURE_STATUSES)[number];

// Portal magic-link tokens (Block 2 B2.6). Scope is enforced by portalTokenMiddleware.
// "feedback" = post-trip rating links (Block 5.7 Řez 4) — type-only addition.
export const PORTAL_SCOPES = ["portal", "offer", "sign", "guide", "feedback"] as const;
export type PortalScope = (typeof PORTAL_SCOPES)[number];

export const PORTAL_SUBJECT_TYPES = ["customer", "guide", "signer"] as const;
export type PortalSubjectType = (typeof PORTAL_SUBJECT_TYPES)[number];

// Guide PWA (Block 2 B2.6) — field incident + expense reporting.
export const INCIDENT_TYPES = ["delay", "illness", "change", "complaint", "other"] as const;
export type IncidentType = (typeof INCIDENT_TYPES)[number];

export const INCIDENT_SEVERITIES = ["low", "medium", "high"] as const;
export type IncidentSeverity = (typeof INCIDENT_SEVERITIES)[number];

export const EXPENSE_CATEGORIES = [
  "transport",
  "food",
  "accommodation",
  "activity",
  "tips",
  "other",
] as const;
export type ExpenseCategory = (typeof EXPENSE_CATEGORIES)[number];

export const INCIDENT_TYPE_LABELS: Record<IncidentType, string> = {
  delay: "Delay",
  illness: "Illness / medical",
  change: "Itinerary change",
  complaint: "Complaint",
  other: "Other",
};

export const INCIDENT_SEVERITY_LABELS: Record<IncidentSeverity, string> = {
  low: "Low",
  medium: "Medium",
  high: "High",
};

export const EXPENSE_CATEGORY_LABELS: Record<ExpenseCategory, string> = {
  transport: "Transport",
  food: "Food & drink",
  accommodation: "Accommodation",
  activity: "Activity",
  tips: "Tips",
  other: "Other",
};

// Complaints (Block 2 B2.7).
export const COMPLAINT_TYPES = [
  "service_quality",
  "delay",
  "refund",
  "cancellation",
  "billing",
  "other",
] as const;
export type ComplaintType = (typeof COMPLAINT_TYPES)[number];

export const COMPLAINT_STATUSES = ["open", "in_progress", "resolved", "rejected"] as const;
export type ComplaintStatus = (typeof COMPLAINT_STATUSES)[number];

export const COMPLAINT_TYPE_LABELS: Record<ComplaintType, string> = {
  service_quality: "Service quality",
  delay: "Delay",
  refund: "Refund request",
  cancellation: "Cancellation",
  billing: "Billing",
  other: "Other",
};

export const COMPLAINT_STATUS_LABELS: Record<ComplaintStatus, string> = {
  open: "Open",
  in_progress: "In progress",
  resolved: "Resolved",
  rejected: "Rejected",
};

// ============================================================================
// HUMAN-READABLE LABELS — single source of truth for UI display.
// Keep keys aligned with the corresponding enum values above.
// ============================================================================

export const TRIP_TYPE_LABELS: Record<TripType, string> = {
  guided: "Guided tour",
  private: "Private trip",
  resort: "Resort stay",
};

export const TRIP_STYLE_LABELS: Record<TripStyle, string> = {
  relaxation: "Beach & Relaxation",
  cultural: "Culture & Heritage",
  adventure: "Adventure & Trekking",
  family: "Family-friendly",
  foodie: "Food & Local Life",
  wellness: "Wellness",
  nature: "Nature & Wildlife",
  cruises: "Cruises",
};

// Hero quick-filter categories (apps/web Hero.astro). Each maps the chosen category to the
// CANONICAL /trips query param(s): 6 public themes (→ `tripStyle`, a SOFT filter) + 2 length
// buckets (→ `duration`, a HARD filter — `multi` is the virtual ">=2 days" value the /trips page
// already handles). Single source of truth so hero, sidebar and megamenu all speak one language.
export const HERO_TRIP_CATEGORIES: { label: string; params: Record<string, string> }[] = [
  ...PUBLIC_TRIP_STYLES.map((key) => ({
    label: TRIP_STYLE_LABELS[key],
    params: { tripStyle: key },
  })),
  { label: "Day trips", params: { duration: "day" } },
  { label: "Multi-day tours", params: { duration: "multi" } },
];

export const DIFFICULTY_LABELS: Record<DifficultyLevel, string> = {
  easy: "Easy",
  medium: "Medium",
  hard: "Hard",
};

export const DESTINATION_REGION_LABELS: Record<DestinationRegion, string> = {
  northern: "Northern Vietnam",
  central: "Central Vietnam",
  southern: "Southern Vietnam",
};

export const ACTIVITY_TYPE_LABELS: Record<ActivityType, string> = {
  water: "Water Sports & Cruises",
  adventure: "Adventure & Trekking",
  culture: "Culture & Heritage",
  nature: "Nature & Wildlife",
  food: "Food & Culinary",
  wellness: "Wellness & Relaxation",
};

export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  inquiry: "Inquiry",
  quoted: "Quoted",
  confirmed: "Confirmed",
  deposit_paid: "Deposit paid",
  fully_paid: "Fully paid",
  completed: "Completed",
  cancelled: "Cancelled",
};

export const LEAD_STATUS_LABELS: Record<LeadStatus, string> = {
  received: "Received",
  quoted: "Quoted",
  call_back: "Call back",
  dormant: "Dormant",
  sold: "Sold",
  lost: "Lost",
};

export const SUPPLIER_TYPE_LABELS: Record<SupplierType, string> = {
  hotel: "Hotel",
  airline: "Airline",
  insurance: "Insurance",
  transport: "Transport",
  guide: "Local guide",
  activity: "Activity vendor",
  visa: "Visa agent",
  other: "Other",
};

export const SUPPLIER_PAYMENT_PROFILE_LABELS: Record<SupplierPaymentProfile, string> = {
  invoice: "Invoice (net terms)",
  cash_offline: "Cash on the spot",
  online: "Online / card",
  prepaid: "Prepaid",
};

export const SERVICE_KIND_LABELS: Record<ServiceKind, string> = {
  flight: "Flight",
  hotel: "Hotel",
  insurance: "Insurance",
  activity: "Activity",
  visa: "Visa",
  transfer: "Transfer",
  other: "Other",
};

export const COST_SCOPE_LABELS: Record<CostScope, string> = {
  per_departure: "Per departure (fixed)",
  per_pax: "Per person",
};

export const BOOKING_MODE_LABELS: Record<BookingMode, string> = {
  confirm_required: "Confirm with supplier",
  on_demand: "On demand — own / always available",
};

export const TRIP_COMPONENT_SEED_TARGET_LABELS: Record<TripComponentSeedTarget, string> = {
  order: "Per order",
  departure: "Per departure (shared)",
};

export const SERVICE_BOOKING_STATUS_LABELS: Record<ServiceBookingStatus, string> = {
  draft: "Draft",
  requested: "Requested",
  confirmed: "Confirmed",
  paid: "Paid",
  cancelled: "Cancelled",
};

export const FLIGHT_ALLOTMENT_STATUS_LABELS: Record<FlightAllotmentStatus, string> = {
  active: "Active",
  released: "Released",
  ticketed: "Ticketed",
  cancelled: "Cancelled",
};

export const TASK_KIND_LABELS: Record<TaskKind, string> = {
  readiness: "Readiness",
  deadline: "Deadline",
  callback: "Call back",
  manual: "Task",
  supplier_followup: "Supplier follow-up",
};

export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  open: "Open",
  done: "Done",
  snoozed: "Snoozed",
  cancelled: "Cancelled",
};

export const TASK_ENTITY_TYPE_LABELS: Record<TaskEntityType, string> = {
  order: "Order",
  lead: "Inquiry",
  service_booking: "Service",
  departure: "Departure",
  customer: "Customer",
  invoice: "Invoice",
  flight_allotment: "Flight allotment",
};

export const INVOICE_TYPE_LABELS: Record<InvoiceType, string> = {
  proforma: "Proforma",
  final: "Final invoice",
  insurance: "Insurance",
  credit_note: "Credit note",
};

export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  issued: "Issued",
  paid: "Paid",
  cancelled: "Cancelled",
};

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  bank: "Bank transfer",
  cash: "Cash",
  stripe: "Card (Stripe)",
  qr: "QR payment",
  other: "Other",
};

export const SUPPLIER_INVOICE_STATUS_LABELS: Record<SupplierInvoiceStatus, string> = {
  received: "Received",
  approved: "Approved",
  paid: "Paid",
  disputed: "Disputed",
};

export const DOCUMENT_KIND_LABELS: Record<DocumentKind, string> = {
  contract: "Contract",
  voucher: "Voucher",
  itinerary: "Itinerary",
  package: "Travel package",
  supplier_confirmation: "Supplier confirmation",
  offer: "Offer",
  confirmation: "Booking confirmation",
  insurance: "Insurance policy",
  other: "Other",
};

export const DOCUMENT_AUDIENCE_LABELS: Record<DocumentAudience, string> = {
  client: "Customer",
  guide: "Guide",
  public: "Public",
  internal: "Internal",
};

export const DOCUMENT_STATUS_LABELS: Record<DocumentStatus, string> = {
  draft: "Draft",
  sent: "Sent",
  viewed: "Viewed",
  signed: "Signed",
  void: "Void",
};

// Sentinel a faceted filter uses to mean "this column IS NULL" (e.g. unassigned
// leads). Shared so the admin FacetPanel and the API route agree on the literal.
export const FACET_NULL = "__none__";

export const BLOG_CATEGORIES = [
  "travel-tips",
  "culture",
  "food",
  "nature",
  "guides",
  "news",
] as const;
export type BlogCategory = (typeof BLOG_CATEGORIES)[number];

export const BLOG_STATUSES = ["draft", "published", "archived"] as const;
export type BlogStatus = (typeof BLOG_STATUSES)[number];

export const VOUCHER_STATUSES = ["active", "used", "expired"] as const;
export type VoucherStatus = (typeof VOUCHER_STATUSES)[number];

export const NEWSLETTER_STATUSES = ["draft", "scheduled", "sending", "sent"] as const;
export type NewsletterStatus = (typeof NEWSLETTER_STATUSES)[number];

// ============================================================================
// DESTINATION
// ============================================================================

export interface Destination {
  id: string;
  slug: string;
  name: string;
  nameSk?: string;
  continent: Continent;
  shortDescription?: string;
  shortDescriptionSk?: string;
  description?: string;
  descriptionSk?: string;
  bestTimeToVisit?: string;
  climateInfo?: string;
  practicalInfo?: {
    visa?: string;
    currency?: string;
    language?: string;
    timezone?: string;
    electricity?: string;
    vaccination?: string;
  };
  highlights?: Array<{
    title: string;
    description: string;
    image?: string;
    imageId?: string | null;
  }>;
  regions?: Array<{
    name: string;
    description: string;
    image?: string;
    imageId?: string | null;
  }>;
  cuisine?: string;
  cuisineSk?: string;
  activities?: string[];
  whyWithUs?: Array<string | { title: string; description: string }>;
  socialReels?: Array<{
    thumbnail?: string;
    thumbnailId?: string | null;
    title: string;
    href?: string;
  }>;
  testimonial?: {
    quote?: string;
    author?: string;
    authorImage?: string;
    authorImageId?: string | null;
    backgroundImage?: string;
    backgroundImageId?: string | null;
  };
  videoPromo?: {
    title?: string;
    description?: string;
    thumbnail?: string;
    thumbnailId?: string | null;
    ctaText?: string;
  };
  weatherData?: {
    bestMonths: number[];
    goodMonths: number[];
    temperatures: Record<string, { air: number; water: number }>;
    description?: string;
  };
  featuredImage?: string;
  galleryImages?: string[];
  heroVideoUrl?: string;
  additionalContent?: string;
  additionalImage?: string;
  // New media system: FK id columns + runtime-populated media objects (additive)
  featuredImageId?: string | null;
  galleryImageIds?: string[];
  additionalImageId?: string | null;
  featuredImageMedia?: MediaResponse | null;
  galleryMedia?: MediaResponse[];
  additionalImageMedia?: MediaResponse | null;
  latitude?: string;
  longitude?: string;
  parentId?: string;
  guideId?: string;
  garantId?: string | null;
  /** Default calculation pax (Block 5.1) — prefill for trip-calculation occupancy. */
  defaultMinPax?: number | null;
  /** Lead auto-routing opt-out (Block 5.7) — false keeps this destination's leads unassigned. */
  autoRouteLeads?: boolean;
  metaTitle?: string;
  metaDescription?: string;
  heroTitle?: string;
  heading?: string;
  faqs?: Array<{ topic: string; items: Array<{ question: string; answer: string }> }>;
  relatedTrips?: string[];
  relatedInspirations?: string[];
  sortOrder: number;
  isFeatured: boolean;
  createdAt: Date;
  updatedAt: Date;
}

// ============================================================================
// GUIDE
// ============================================================================

export interface Guide {
  id: string;
  slug: string;
  name: string;
  photo?: string;
  photoMediaId?: string | null;
  photoMedia?: MediaResponse | null;
  bio?: string;
  bioSk?: string;
  livingIn?: string;
  homeLocationId?: string | null;
  yearsExperience?: number;
  specializations?: string[];
  youtubeChannelUrl?: string;
  featuredVideos?: string[];
  email?: string;
  phone?: string;
  whatsapp?: string;
  isActive: boolean;
  sortOrder: number;
  createdAt: Date;
  updatedAt: Date;
}

// ============================================================================
// LOCATION + GUIDE SCHEDULE (Block 4.2)
// ============================================================================

export interface Location {
  id: string;
  name: string;
  region?: string | null;
  lat?: number | null;
  lng?: number | null;
  sortOrder: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface LocationGap {
  fromLocationId: string;
  toLocationId: string;
  minGapDays: number;
}

export interface GuideAssignment {
  id: string;
  guideId: string;
  tripDateId: string;
  role: GuideAssignmentRole;
  status: GuideAssignmentStatus;
  notes?: string | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface GuideAvailability {
  id: string;
  guideId: string;
  fromDate: string; // ISO YYYY-MM-DD (inclusive)
  toDate: string; // ISO YYYY-MM-DD (inclusive)
  reason: GuideAvailabilityReason;
  notes?: string | null;
  createdAt: Date;
  updatedAt: Date;
}

// ============================================================================
// ACTIVITY
// ============================================================================

export interface Activity {
  id: string;
  slug: string;
  title: string;
  shortDescription?: string;
  description?: string;
  featuredImage?: string;
  galleryImages?: string[];
  featuredImageId?: string | null;
  galleryImageIds?: string[];
  testimonialAuthorImageId?: string | null;
  featuredImageMedia?: MediaResponse | null;
  galleryMedia?: MediaResponse[];
  testimonialAuthorImageMedia?: MediaResponse | null;
  duration?: string;
  priceFrom?: number;
  difficultyLevel?: DifficultyLevel;
  activityType?: ActivityType;
  destinationId?: string;
  location?: string;
  included?: string[];
  tags?: string[];
  videos?: Array<{ url: string; title: string }>;
  tipsContent?: string;
  tips?: Array<{ question: string; answer: string }>;
  testimonialQuote?: string;
  testimonialAuthor?: string;
  testimonialAuthorImage?: string;
  socialReels?: Array<{
    thumbnail?: string;
    thumbnailId?: string | null;
    title: string;
    href?: string;
  }>;
  relatedItems?: string[];
  heroTitle?: string;
  heading?: string;
  isFeatured: boolean;
  status: TripStatus;
  sortOrder: number;
  metaTitle?: string;
  metaDescription?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface ActivityWithRelations extends Activity {
  destination?: Destination;
}

// ============================================================================
// TRIP
// ============================================================================

export interface Trip {
  id: string;
  slug: string;
  title: string;
  titleSk?: string;
  shortDescription?: string;
  shortDescriptionSk?: string;
  description?: string;
  descriptionSk?: string;
  priceFrom: number;
  priceOriginal?: number;
  priceEur?: number;
  durationDays?: number;
  durationHours?: number;
  difficultyLevel?: DifficultyLevel;
  groupSizeMin: number;
  groupSizeMax: number;
  tripType: TripType;
  tripKind: TripKind;
  /** Booking axis (Pre-launch Area 3) — fixed_departures (dropdown) vs on_request (open calendar).
   *  Optional for back-compat; resolve with isOnRequestBooking(trip), never raw tripKind. */
  fulfillmentMode?: FulfillmentMode;
  /** ISO-2 countries the trip touches (Block 5.6) — absent reads as ["VN"]. */
  countryCodes?: string[] | null;
  /** Facultative on-site activities for the offer PDF (Block 5.6 Řez 2). */
  optionalActivities?: Array<{ name: string; priceNote?: string; description?: string }> | null;
  availableWeekdays?: number[];
  leadTimeHours: number;
  highlights?: Array<{
    title: string;
    description?: string;
    icon?: string;
    image?: string;
    imageId?: string | null;
  }>;
  schedule?: Array<{ time: string; title: string; description?: string }>;
  tripStyle?: TripStyle;
  guideId?: string;
  destinationId?: string;
  startLocationId?: string | null;
  endLocationId?: string | null;
  featuredImage?: string;
  galleryImages?: string[];
  youtubeVideos?: string[];
  guideVideoUrl?: string;
  featuredImageId?: string | null;
  galleryImageIds?: string[];
  mapImageId?: string | null;
  featuredImageMedia?: MediaResponse | null;
  galleryMedia?: MediaResponse[];
  mapImageMedia?: MediaResponse | null;
  itinerary?: ItineraryDay[];
  included?: string[];
  excluded?: string[];
  faqs?: Array<{ topic: string; items: Array<{ question: string; answer: string }> }>;
  socialReels?: Array<{
    thumbnail?: string;
    thumbnailId?: string | null;
    title: string;
    href?: string;
  }>;
  mapImage?: string;
  relatedItems?: string[];
  heroTitle?: string;
  heading?: string;
  isFeatured: boolean;
  isLastMinute: boolean;
  hasCzechGuide: boolean;
  allowsOnlineBooking: boolean;
  note?: string;
  guideRecommendation?: string;
  defaultMeetingPoint?: string | null; // tour-standard sraz place (Block 4.4) — inherited by departures
  defaultMeetingTime?: string | null; // tour-standard sraz time (Block 4.4)
  tags?: string[];
  metaTitle?: string;
  metaDescription?: string;
  status: TripStatus;
  createdAt: Date;
  updatedAt: Date;
}

export interface ItineraryDay {
  day: number;
  title: string;
  description?: string;
  image?: string;
  imageId?: string | null;
  accommodation?: string;
  meals?: string[];
  highlights?: string[];
  guideTip?: string;
}

// ============================================================================
// TRIP DATE
// ============================================================================

export interface TripDate {
  id: string;
  tripId: string;
  startDate: Date;
  endDate: Date;
  price: number;
  priceEur?: number;
  priceSingleRoom?: number;
  capacity: number;
  bookedCount: number;
  guideId?: string;
  lifecycle: TripDateLifecycle;
  occupancy?: OccupancyLevel; // derived on read, not stored
  code?: string | null; // human departure code (e.g. "2610AUWA")
  leadUserId?: string | null; // garant (departure owner)
  minPax?: number | null; // hard go/no-go threshold (not enforced)
  notes?: string;
  guideMessage?: string | null; // personal guide message → customer portal (Block 4.3)
  meetingPoint?: string | null; // sraz override (Block 4.4) — falls back to trip.defaultMeetingPoint
  meetingTime?: string | null; // sraz time override (Block 4.4) — falls back to trip.defaultMeetingTime
  meetingNote?: string | null; // departure-specific meeting instructions (Block 4.4)
  createdAt: Date;
  updatedAt: Date;
}

/** Resolved meeting point / sraz (Block 4.4, Téma 7) — see apps/api/src/lib/meeting.ts. */
export interface MeetingInfo {
  point: string | null;
  time: string | null;
  note: string | null;
}

// ============================================================================
// CUSTOMER
// ============================================================================

export interface Customer {
  id: string;
  email: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  street?: string;
  city?: string;
  zip?: string;
  country: string;
  birthDate?: Date;
  passportNumber?: string;
  passportExpiry?: Date;
  nationality?: string;
  totalSpent: number;
  tripsCount: number;
  firstTripDate?: Date;
  lastTripDate?: Date;
  score: number;
  newsletterSubscribed: boolean;
  newsletterPreferences?: string[];
  notes?: string;
  source?: string;
  createdAt: Date;
  updatedAt: Date;
}

// ============================================================================
// ORDER
// ============================================================================

export interface Order {
  id: string;
  orderNumber: string;
  customerId: string;
  tripId?: string | null;
  tripDateId?: string;
  leadId?: string | null;
  requestedDate?: string;
  status: OrderStatus;
  participants: Participant[];
  adultsCount: number;
  childrenCount: number;
  totalPrice: number;
  currency: string;
  depositAmount?: number;
  depositDueDate?: Date;
  depositPaidAt?: Date;
  fullPaymentDue?: Date;
  fullPaymentPaidAt?: Date;
  stripePaymentIntentId?: string;
  stripeCustomerId?: string;
  contractUrl?: string;
  invoiceUrl?: string;
  source: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  // Ad click attribution (B3.4) — inherited from the lead on conversion, carried for the feed.
  clickId?: string | null;
  clickSource?: string | null;
  assignedTo?: string;
  customerNotes?: string;
  internalNotes?: string;
  // Cancellation (Block 2 B2.7).
  cancelledAt?: string | null;
  cancellationMeta?: Record<string, unknown> | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface Participant {
  name: string;
  birthDate?: string;
  passport?: string;
  passportExpiry?: string;
  nationality?: string;
}

export interface OrderItem {
  id: string;
  orderId: string;
  kind: OrderItemKind;
  tripId?: string | null;
  tripDateId?: string | null;
  name: string;
  description?: string | null;
  unitPrice: number;
  quantity: number;
  currency: string;
  meta?: Record<string, unknown> | null;
  sortOrder: number;
  createdAt?: string;
  updatedAt?: string;
}

// ============================================================================
// LEAD (inquiry funnel)
// ============================================================================

export interface Lead {
  id: string;
  leadNumber: string;
  status: LeadStatus;
  customerId?: string | null;
  contactName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  tripId?: string | null;
  destinationId?: string | null;
  requestedDate?: string | null;
  paxAdults: number;
  paxChildren: number;
  estimatedValue?: number | null;
  currency: string;
  assignedTo?: string | null;
  source: string;
  utmSource?: string | null;
  utmMedium?: string | null;
  utmCampaign?: string | null;
  firstTouchSource?: string | null;
  firstTouchCampaign?: string | null;
  lastTouchSource?: string | null;
  lastTouchCampaign?: string | null;
  clickId?: string | null;
  nextActionAt?: string | null;
  leadScore: number;
  lostReason?: string | null;
  convertedOrderId?: string | null;
  /** Enriched inquiry profile + routing stamp (Block 5.7). */
  profile?: {
    tripType?: "fit" | "guided" | "stay" | "service";
    flightClass?: "economy" | "premium" | "business";
    budget?: string;
    dateFlexibility?: "fixed" | "plus_minus_week" | "flexible";
    travelExperience?: "first_asia" | "experienced" | "expert";
    childAges?: number[];
    routing?: { by: "garant" | "round_robin"; destinationId?: string; at: string };
  } | null;
  aiDraft?: {
    draft?: string;
    confidence?: number;
    fields?: Record<string, { value: unknown; confidence: number }>;
  } | null;
  customerNotes?: string | null;
  internalNotes?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface LeadWithRelations extends Lead {
  assignedToName?: string | null;
  destinationName?: string | null;
  tripTitle?: string | null;
  customer?: Pick<Customer, "id" | "firstName" | "lastName" | "email" | "phone"> | null;
}

// ============================================================================
// COMMUNICATION
// ============================================================================

// Block 5.5 — AI call-transcript metadata on a channel='phone' communication.
export interface CommCallMeta {
  source: string;
  summary: string;
  topics?: string[];
  callerName?: string;
  callerPhone?: string;
  destination?: string;
  budget?: string;
  requestedDate?: string;
  followUpAt?: string;
  followUpNote?: string;
  followUpAppliedAt?: string;
  recordingUrl?: string;
  at: string;
}

export interface Communication {
  id: string;
  orderId?: string;
  leadId?: string;
  customerId: string;
  type: CommunicationType;
  direction: CommunicationDirection;
  channel?: string;
  /** Provider conversation id (Gmail threadId) — lets an admin reply thread back to the lead. */
  threadId?: string;
  subject?: string;
  content?: string;
  emailMessageId?: string;
  emailStatus?: string;
  attachments?: string[];
  meta?: { call?: CommCallMeta } | null;
  createdBy?: string;
  createdAt: Date;
}

// ============================================================================
// USER (Admin)
// ============================================================================

export interface User {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  isActive: boolean;
  /** Specialist queue profile (E2E-018) — service-booking kinds this user works. */
  queueKinds?: ServiceKind[];
  lastLoginAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

// ============================================================================
// REVIEW
// ============================================================================

export interface Review {
  id: string;
  tripId?: string;
  destinationId?: string;
  guideId?: string;
  customerName: string;
  customerLocation?: string;
  rating: number;
  title?: string;
  content: string;
  tripDate?: Date;
  isFeatured: boolean;
  isVideo: boolean;
  videoUrl?: string;
  images?: string[];
  imageIds?: string[];
  isApproved: boolean;
  createdAt: Date;
}

// ============================================================================
// PREMIUM UPGRADE
// ============================================================================

export interface PremiumUpgrade {
  id: string;
  title: string;
  description?: string;
  image?: string;
  imageId?: string | null;
  badge?: string;
  sortOrder: number;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface TripPremiumUpgrade {
  id: string;
  tripId: string;
  upgradeId: string;
  sortOrder: number;
  createdAt: Date;
}

// ============================================================================
// NEWSLETTER
// ============================================================================

export interface NewsletterSubscriber {
  id: string;
  email: string;
  preferences?: string[];
  isActive: boolean;
  confirmedAt?: Date;
  confirmationToken?: string;
  source?: string;
  unsubscribedAt?: Date;
  unsubscribeReason?: string;
  createdAt: Date;
}

export interface NewsletterCampaign {
  id: string;
  subject: string;
  subjectSk?: string;
  content: string;
  contentSk?: string;
  aiGenerated: boolean;
  aiPrompt?: string;
  sentCount: number;
  openedCount: number;
  clickedCount: number;
  status: NewsletterStatus;
  scheduledAt?: Date;
  sentAt?: Date;
  createdBy?: string;
  createdAt: Date;
}

// ============================================================================
// GIFT VOUCHER
// ============================================================================

export interface GiftVoucher {
  id: string;
  code: string;
  amount: number;
  currency: string;
  buyerName: string;
  buyerEmail: string;
  recipientName?: string;
  recipientEmail?: string;
  personalMessage?: string;
  designTemplate: string;
  status: VoucherStatus;
  usedAt?: Date;
  usedOrderId?: string;
  expiresAt: Date;
  pdfUrl?: string;
  sentAt?: Date;
  createdAt: Date;
}

// ============================================================================
// QUIZ
// ============================================================================

export interface QuizResult {
  id: string;
  answers: Record<string, string>;
  recommendedDestinations: string[];
  email?: string;
  subscribedNewsletter: boolean;
  createdAt: Date;
}

// ============================================================================
// PAGE VIEW (Analytics)
// ============================================================================

export interface PageView {
  id: string;
  visitorId: string;
  pageType: string;
  pageId?: string;
  createdAt: Date;
}

// ============================================================================
// BLOG POST
// ============================================================================

export interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt?: string;
  content?: string;
  featuredImage?: string;
  galleryImages?: string[];
  category: BlogCategory;
  tags?: string[];
  destinationId?: string;
  relatedTripIds?: string[];
  authorName?: string;
  authorAvatar?: string;
  featuredImageId?: string | null;
  galleryImageIds?: string[];
  authorAvatarId?: string | null;
  featuredImageMedia?: MediaResponse | null;
  galleryMedia?: MediaResponse[];
  authorAvatarMedia?: MediaResponse | null;
  readTime?: number;
  isFeatured: boolean;
  status: BlogStatus;
  publishedAt?: string;
  sortOrder: number;
  metaTitle?: string;
  metaDescription?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface BlogPostWithRelations extends BlogPost {
  destination?: Destination;
  destinationName?: string;
  destinationSlug?: string;
  relatedTrips?: Trip[];
  relatedPosts?: BlogPost[];
}

// ============================================================================
// DESTINATION TEAM
// ============================================================================

export const TEAM_SECTION_TYPES = ["guarantor", "sales_team", "guide", "local_team"] as const;
export type TeamSectionType = (typeof TEAM_SECTION_TYPES)[number];

export interface DestinationTeamSection {
  id: string;
  destinationId: string;
  sectionType: TeamSectionType;
  name: string;
  subtitle?: string;
  bio?: string;
  email?: string;
  phone?: string;
  photo?: string;
  photoMediaId?: string | null;
  photos?: string[];
  photoIds?: string[];
  members?: Array<{
    name: string;
    role?: string;
    bio?: string;
    photo?: string;
    photoId?: string | null;
  }>;
  sortOrder: number;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

// ============================================================================
// FAQ
// ============================================================================

export interface FAQ {
  id: string;
  question: string;
  answer: string;
  topic: string;
  topicSortOrder: number;
  sortOrder: number;
  status: "draft" | "published";
  createdAt: Date;
  updatedAt: Date;
}

// ============================================================================
// API RESPONSE TYPES
// ============================================================================

export interface ApiResponse<T> {
  data: T;
  success: true;
}

export interface ApiError {
  error: string;
  message: string;
  success: false;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  success: true;
}

// ============================================================================
// EXTENDED TYPES (with relations)
// ============================================================================

export interface TripWithRelations extends Trip {
  destination?: Destination;
  guide?: Guide;
  dates?: TripDate[];
  reviews?: Review[];
  upgrades?: PremiumUpgrade[];
}

export interface DestinationWithRelations extends Destination {
  guide?: Guide;
  trips?: Trip[];
  parent?: Destination;
  children?: Destination[];
}

export interface GuideWithRelations extends Guide {
  destinations?: Destination[];
  trips?: Trip[];
}

export interface OrderWithRelations extends Order {
  customer?: Customer;
  trip?: Trip;
  tripDate?: TripDate;
  communications?: Communication[];
}
